import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import styles from './DataGridEmptyState.module.css';

export interface DataGridEmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  /** Why it is empty and what to do next. An empty state without this is a wall. */
  description?: ReactNode;
  /** The one action that resolves the emptiness. */
  action?: ReactNode;
  /**
   * `filtered` when a query is responsible — the copy and the action differ from
   * a genuinely empty catalogue, and so does what the user should try next.
   */
  reason?: 'empty' | 'filtered' | 'error';
  compact?: boolean;
}

const REASON_LABEL = {
  empty: 'No data',
  filtered: 'No matches',
  error: 'Could not load',
} as const;

/**
 * Announced with `role="status"`: after a filter change the grid body swaps for
 * this, and a screen-reader user gets no visual cue that it happened.
 */
export const DataGridEmptyState = forwardRef<HTMLDivElement, DataGridEmptyStateProps>(
  function DataGridEmptyState(
    { title, description, action, reason = 'empty', compact = false, className, ...rest },
    ref,
  ) {
    return (
      <div
        {...rest}
        ref={ref}
        role="status"
        data-reason={reason}
        className={clsx(styles.empty, compact && styles.compact, className)}
      >
        <p className={styles.eyebrow}>{REASON_LABEL[reason]}</p>
        <p className={styles.title}>{title}</p>
        {description !== undefined && <p className={styles.description}>{description}</p>}
        {action !== undefined && <div className={styles.action}>{action}</div>}
      </div>
    );
  },
);
